// Client-side JS port of the Python fill-mode builders in
// Automation-Scripts/Inventory/seed_fridge_inventory.py. Kept separate so the
// page can render the planned state without a round-trip to the backend, and
// so the logic can be unit tested in isolation.
//
// IMPORTANT: if the Python side changes, update this file too.

import { CST_TIMEZONE, MS_PER_DAY, uuidv4, cstNow } from './seedUtils';

export const FILL_MODES = {
  LAYOUT_DEFAULT: 'LAYOUT_DEFAULT',
  ALL_FRESH: 'ALL_FRESH',
  ALL_EXPIRING_SOON: 'ALL_EXPIRING_SOON',
  ALL_EXPIRED: 'ALL_EXPIRED',
  MIXED_SHELF_LIFE: 'MIXED_SHELF_LIFE',
  HALF_EMPTY: 'HALF_EMPTY',
  EMPTY: 'EMPTY',
  // FROM_CONFIG is intentionally excluded from the CMS — CLI only.
};

export const FILL_MODE_DESCRIPTIONS = {
  LAYOUT_DEFAULT: {
    label: 'Layout Default',
    description:
      'Every slot filled to its maxPar with the layout\'s vendItem. Production is backdated so items expire ~shelf-life-minus-12h from now. The baseline "normal stocking" scenario.',
    allowsShelfLife: false,
    allowsFillCount: true,
  },
  ALL_FRESH: {
    label: 'All Fresh',
    description:
      'Same as Layout Default but with a custom shelf life (default 14 days). Items expire in (N − 12h) from now. Use for fresh-stock regressions.',
    allowsShelfLife: true,
    allowsFillCount: true,
  },
  ALL_EXPIRING_SOON: {
    label: 'All Expiring Soon',
    description:
      "Raw expirationDate set to yesterday so the kiosk-side date (arch InventoryUtils adds +2d) lands tomorrow — the kiosk's 24-hour Expiring Items Discount window. Also requires the `expiring-items` LaunchDarkly flag on the test user and a pricing schema with an `expiring_items` discount tag, otherwise the discount UI stays hidden.",
    allowsShelfLife: false,
    allowsFillCount: true,
  },
  ALL_EXPIRED: {
    label: 'All Expired',
    description:
      'Raw expirationDate set to 3 days ago, status = EXPIRED. Kiosk-side date (+2d) lands yesterday, so the CMS inventory page reads "expired" and the kiosk\'s 24h discount window excludes the items. Use for expired-item handling tests.',
    allowsShelfLife: false,
    allowsFillCount: true,
  },
  MIXED_SHELF_LIFE: {
    label: 'Mixed Shelf Life',
    description:
      'Within each slot, stagger expirations across (1, 3, 7, full shelf) days minus 12h (cycling, capped at full). A realistic "real fridge" state.',
    allowsShelfLife: false,
    allowsFillCount: true,
  },
  HALF_EMPTY: {
    label: 'Half Empty',
    description:
      'Every slot filled to ceil(maxPar / 2). Use for low-stock flows.',
    allowsShelfLife: false,
    allowsFillCount: false,
  },
  EMPTY: {
    label: 'Empty',
    description: 'Every slot zeroed. Clears the fridge.',
    allowsShelfLife: false,
    allowsFillCount: false,
  },
};

const EMPTY_SLOT_VEND_ITEM = 'EMPTY_SLOT_NEW';
const DEFAULT_FRESH_SHELF_LIFE_DAYS = 14;
// Shift the nominal "expires in N days" anchor back by 12h so a mode that
// reads as "expiring tomorrow" actually expires in 12 hours, "expired
// yesterday" actually expired 36 hours ago, etc.
const EXPIRATION_HOURS_OFFSET_MS = 12 * 60 * 60 * 1000;

// Convert a JS Date into an epoch-ms timestamp as if the Date were wall-clock
// time in America/Chicago. Mirrors the Python set_cst_convert_to_epoch so fridge
// rendering matches a real stocking.
export function cstEpochMs(date) {
  // `toLocaleString('en-US', {timeZone: 'America/Chicago'})` gives us a string
  // representation of the *same wall-clock time* in Chicago. Then we parse it
  // back to a Date using the host timezone and read its epoch ms.
  //
  // In practice, the only caller is a Date produced by `new Date()` (now).
  // For "produced yesterday in Chicago" semantics, we shift in day granularity
  // using the production / expiration offsets below, not DST-sensitive logic.
  const iso = new Date(
    date.toLocaleString('en-US', { timeZone: CST_TIMEZONE }),
  ).getTime();
  return iso;
}

function makeItem(vendItemName, productionDate, expirationDate, status) {
  return {
    uniqueId: uuidv4(),
    vendItemName,
    productionDate: productionDate.getTime(),
    expirationDate: expirationDate.getTime(),
    status,
  };
}

function emptySlot() {
  return { itemsInSlot: [] };
}

function isFillable(slot) {
  const n = slot.vendItemName;
  return !!n && n !== EMPTY_SLOT_VEND_ITEM;
}

function resolveFillCount(slot, override) {
  if (override != null) return Math.max(0, Number(override));
  return Math.max(0, Number(slot.maxPerSlot || 0));
}

// Target expiration moment for a mode whose nominal offset is N days from now.
// Shifts the anchor back 12h so the seeded items expire mid-day rather than at
// the day boundary, matching how real fridges expire stock.
function targetExpiration(now, daysFromNow) {
  return new Date(
    now.getTime() + daysFromNow * MS_PER_DAY - EXPIRATION_HOURS_OFFSET_MS,
  );
}

// Expiration anchored to noon of (today + daysFromToday) in CST. Use this
// when the *date* portion of the expiration matters — the CMS inventory page
// and the kiosk's Expiring Items Discount rule both key off the date (after a
// backend +2d shift in arch InventoryUtils.getExpirations), so a target like
// "yesterday" must land squarely on yesterday's calendar day instead of
// drifting across midnight as `now + N*24h − 12h` would.
function expirationOnDay(now, daysFromToday) {
  const d = new Date(now);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + daysFromToday);
  return d;
}

// Production date = expiration − shelf life. Reverses the old "production =
// now" anchor so backdated items look realistic instead of having a 1-day
// implied shelf life on "expiring soon" SKUs.
function productionFor(expiration, shelfLifeDays) {
  return new Date(expiration.getTime() - shelfLifeDays * MS_PER_DAY);
}

// ----- public API --------------------------------------------------------

/**
 * Collapse the layout so each vendItemName lives in only one slot (the lowest
 * (x,y) wins). Other slots that would have held that SKU are marked collapsed
 * and render empty, with a pointer back to the slot that kept the SKU.
 */
export function collapseSkuSlots(slots) {
  // First pass: find the primary slot per SKU.
  const primarySlotForSku = {};
  [...slots]
    .sort((a, b) => a.x - b.x || a.y - b.y)
    .forEach((s) => {
      const name = s.vendItemName;
      if (name && name !== EMPTY_SLOT_VEND_ITEM && !primarySlotForSku[name]) {
        primarySlotForSku[name] = `(${s.x},${s.y})`;
      }
    });

  // Second pass: mark duplicates, keeping original order and annotating with
  // both the SKU that was moved and the slot where it now lives.
  return slots.map((s) => {
    const out = { ...s };
    const name = s.vendItemName;
    if (name && name !== EMPTY_SLOT_VEND_ITEM) {
      const primary = primarySlotForSku[name];
      if (primary && primary !== `(${s.x},${s.y})`) {
        out.vendItemName = null;
        out.collapsed = true;
        out.isolatedSku = name;
        out.vendsAt = primary;
      }
    }
    return out;
  });
}

/**
 * Run the selected fill mode against `slots` and `shelfLife`, return a full
 * `itemsBySlot` object with an entry for every layout slot.
 *
 * `opts`: { fillCount?: number, shelfLifeDays?: number }
 */
export function buildItemsBySlot(mode, slots, shelfLife, opts = {}) {
  const now = cstNow();
  const out = {};
  slots.forEach((s) => {
    const fmt = `(${s.x},${s.y})`;
    out[fmt] = isFillable(s)
      ? buildSlot(mode, s, shelfLife, opts, now)
      : emptySlot();
  });
  return out;
}

function buildSlot(mode, slot, shelfLife, opts, now) {
  const count = resolveFillCount(slot, opts.fillCount);
  const sku = slot.vendItemName;
  const fullShelf = shelfLife[sku] ?? 7;

  // Build one item at `daysFromNow` until expiration, with production backdated
  // by `shelfLifeDays` (defaults to the SKU's menu shelf life).
  const itemAt = (daysFromNow, status, shelfLifeDays = fullShelf) => {
    const expiration = targetExpiration(now, daysFromNow);
    const production = productionFor(expiration, shelfLifeDays);
    return makeItem(sku, production, expiration, status);
  };

  // Same shape as itemAt, but anchors the expiration on a specific calendar
  // day at noon — used by modes whose correctness depends on the date portion
  // (the kiosk's discount rule and the CMS inventory page both date-strip).
  const itemOnDay = (daysFromToday, status, shelfLifeDays = fullShelf) => {
    const expiration = expirationOnDay(now, daysFromToday);
    const production = productionFor(expiration, shelfLifeDays);
    return makeItem(sku, production, expiration, status);
  };

  switch (mode) {
    case FILL_MODES.LAYOUT_DEFAULT: {
      return {
        itemsInSlot: Array.from({ length: count }, () =>
          itemAt(fullShelf, 'AVAILABLE'),
        ),
      };
    }
    case FILL_MODES.ALL_FRESH: {
      const days = opts.shelfLifeDays ?? DEFAULT_FRESH_SHELF_LIFE_DAYS;
      return {
        itemsInSlot: Array.from({ length: count }, () =>
          itemAt(days, 'AVAILABLE', days),
        ),
      };
    }
    case FILL_MODES.ALL_EXPIRING_SOON: {
      // Kiosk flags an item as discount-eligible when its kiosk-side date
      // (= raw expirationDate.date + 2d, applied by arch
      // InventoryUtils.getExpirations) is *tomorrow*. So seed raw
      // expirationDate.date = yesterday → kiosk sees tomorrow → flagged.
      return {
        itemsInSlot: Array.from({ length: count }, () =>
          itemOnDay(-1, 'AVAILABLE'),
        ),
      };
    }
    case FILL_MODES.ALL_EXPIRED: {
      // Kiosk-side date = raw + 2d. Anchor 3 days back so the kiosk-side
      // date is yesterday — comfortably past today, so CMS inventory reads
      // "expired" and the kiosk's 24h discount window misses the item.
      return {
        itemsInSlot: Array.from({ length: count }, () =>
          itemOnDay(-3, 'EXPIRED'),
        ),
      };
    }
    case FILL_MODES.MIXED_SHELF_LIFE: {
      const offsets = [
        Math.min(1, fullShelf),
        Math.min(3, fullShelf),
        Math.min(7, fullShelf),
        fullShelf,
      ];
      return {
        itemsInSlot: Array.from({ length: count }, (_, i) =>
          itemAt(offsets[i % offsets.length], 'AVAILABLE'),
        ),
      };
    }
    case FILL_MODES.HALF_EMPTY: {
      const halfCount = count > 0 ? Math.ceil(count / 2) : 0;
      return {
        itemsInSlot: Array.from({ length: halfCount }, () =>
          itemAt(fullShelf, 'AVAILABLE'),
        ),
      };
    }
    case FILL_MODES.EMPTY:
    default:
      return emptySlot();
  }
}

/**
 * Build the full STOCKING queue-message payload (pre-envelope) and a stable
 * updateId we can reuse for the AppSync mutation's messageId.
 */
export function buildQueueMessageData(itemsBySlot) {
  const updateId = uuidv4();
  const now = Date.now();
  return {
    updateId,
    queueMessageData: {
      source: 'STOCKING',
      updateTimestamp: String(now),
      updateId,
      itemsBySlot,
    },
  };
}

// Summary helpers for the planned-state card.
export function summarizeItemsBySlot(itemsBySlot) {
  let totalItems = 0;
  let expiringCount = 0;
  let expiredCount = 0;
  let nonEmptySlots = 0;
  const now = Date.now();
  const threshold = now + 2 * MS_PER_DAY;
  Object.values(itemsBySlot).forEach((slot) => {
    const items = slot.itemsInSlot || [];
    if (items.length > 0) nonEmptySlots += 1;
    totalItems += items.length;
    items.forEach((it) => {
      if (it.status === 'EXPIRED' || it.expirationDate < now) {
        expiredCount += 1;
      } else if (it.expirationDate < threshold) {
        expiringCount += 1;
      }
    });
  });
  return {
    totalItems,
    expiringCount,
    expiredCount,
    nonEmptySlots,
    slotCount: Object.keys(itemsBySlot).length,
  };
}
